import { useState, useEffect } from 'react';
import { canUseDOM, getWindow } from '@surfline/web-common';
import { useRouter } from 'next/router';

import { useDeviceSize } from 'hooks/useDeviceSize';
import type { Camera } from 'types/camera';
import { useUserPermissionStatus } from 'selectors/user';
import { getCanUseLocalStorage } from 'utils/canUseLocalStorage';

interface SpotPreference {
  primaryCamId: string;
  multiCamViewActive: boolean;
}

const STORAGE_KEY = 'camPreferences';

const fetchPreference = (spotId: string): SpotPreference | null => {
  if (!getCanUseLocalStorage()) {
    return null;
  }

  try {
    const preferences = JSON.parse(
      getWindow()?.localStorage?.getItem(STORAGE_KEY) || '{}',
    ) as Record<string, SpotPreference>;
    return preferences?.[spotId] || null;
  } catch (error) {
    console.warn('Failed to parse camera preferences from localStorage:', error);
    return null;
  }
};

const updatePreference = (
  spotId: string,
  primaryCamId: string,
  multiCamViewActive: boolean,
): void => {
  if (!getCanUseLocalStorage()) {
    return;
  }

  try {
    const preferences = JSON.parse(
      getWindow()?.localStorage?.getItem(STORAGE_KEY) || '{}',
    ) as Record<string, SpotPreference>;

    const updatedPreferences = {
      ...preferences,
      [spotId]: {
        primaryCamId,
        multiCamViewActive,
      },
    };

    getWindow()?.localStorage?.setItem(STORAGE_KEY, JSON.stringify(updatedPreferences));
  } catch (error) {
    console.warn('Failed to save camera preferences to localStorage:', error);
  }
};

// Helper function to get the first available camera
const getDefaultCamera = (cameras: Camera[]): Camera =>
  cameras.find((cam) => !cam.status.isDown) || cameras[0];

// Helper function to determine if multi-cam should be active by default
const shouldDefaultToMultiCam = (
  hasMultipleCams: boolean,
  deviceWidth: number,
  hasPremiumCamPermissions: boolean,
): boolean => {
  if (!hasMultipleCams || !canUseDOM) {
    return false;
  }

  const isDesktopOrTablet = deviceWidth >= 512;
  return isDesktopOrTablet && hasPremiumCamPermissions;
};

// Helper function to extract common preference loading logic
const loadCameraPreferences = (
  spotId: string,
  cameras: Camera[],
  hasMultipleCams: boolean,
  urlSetCamId: string | null,
  deviceWidth: number,
  hasPremiumCamPermissions: boolean,
) => {
  const defaultCamera = getDefaultCamera(cameras);

  // If single camera, always use single-cam mode
  if (!hasMultipleCams) {
    return {
      primaryCamId: urlSetCamId || defaultCamera._id,
      multiCamViewActive: false,
    };
  }

  // Try to load saved preferences first
  const savedPreference = fetchPreference(spotId);

  if (savedPreference) {
    // Validate that the saved camera still exists and is working
    const cameraFromPreference = cameras.find((cam) => cam._id === savedPreference.primaryCamId);
    const isPreferredCameraAvailable = cameraFromPreference && !cameraFromPreference.status.isDown;

    // Respect saved preferences over URL state - user interactions take precedence
    return {
      primaryCamId: isPreferredCameraAvailable
        ? savedPreference.primaryCamId
        : urlSetCamId || defaultCamera._id,
      multiCamViewActive: savedPreference.multiCamViewActive,
    };
  }

  // If there's a URL-set camera ID and NO saved preferences, prioritize single-cam mode to respect the URL
  // This handles initial page loads with camera IDs in the URL
  if (urlSetCamId) {
    return {
      primaryCamId: urlSetCamId,
      multiCamViewActive: false,
    };
  }

  // Default logic when no saved preference exists and no URL camera ID
  const defaultToMulti = shouldDefaultToMultiCam(
    hasMultipleCams,
    deviceWidth,
    hasPremiumCamPermissions,
  );

  return {
    primaryCamId: urlSetCamId || defaultCamera._id,
    multiCamViewActive: defaultToMulti,
  };
};

const useCamsPreferenceFromLocalStorage = (
  spotId: string,
  cameras: Camera[],
  hasMultipleCams: boolean,
  shouldUpdateLocalStorage: boolean = false,
) => {
  const router = useRouter();
  const [multiCamLoaded, setMultiCamLoaded] = useState(false);
  const device = useDeviceSize();
  const { hasPremiumCamPermissions } = useUserPermissionStatus();

  const { camId } = router.query as { camId: string; spotId: string };

  const urlSetCam = cameras.filter((cam) => cam._id === camId);
  const urlSetCamId = urlSetCam && urlSetCam.length > 0 ? urlSetCam[0]._id : null;

  const [multiCamViewActive, setMultiCamViewActive] = useState(false);
  const defaultCamera = getDefaultCamera(cameras);
  const [primaryCamId, setPrimaryCamId] = useState(urlSetCamId || defaultCamera._id);

  // Track the previous URL camera ID to detect URL changes
  const [prevUrlSetCamId, setPrevUrlSetCamId] = useState<string | null>(urlSetCamId);

  // Combined effect to handle both initial mount and spot changes
  useEffect(() => {
    // Only update preferences if:
    // 1. It's the initial load (multiCamLoaded is false), OR
    // 2. The URL camera ID has actually changed (not just a re-render with same URL)
    const shouldUpdatePreferences = !multiCamLoaded || prevUrlSetCamId !== urlSetCamId;

    if (!shouldUpdatePreferences) {
      return;
    }

    // Don't reset camera ID if there's a different camera already selected locally
    // This prevents overriding user actions that haven't been reflected in URL yet
    if (multiCamLoaded && urlSetCamId && primaryCamId !== urlSetCamId) {
      setPrevUrlSetCamId(urlSetCamId);
      return;
    }

    const preferences = loadCameraPreferences(
      spotId,
      cameras,
      hasMultipleCams,
      urlSetCamId,
      device.width,
      hasPremiumCamPermissions,
    );

    setPrimaryCamId(preferences.primaryCamId);
    setMultiCamViewActive(preferences.multiCamViewActive);
    setMultiCamLoaded(true);
    setPrevUrlSetCamId(urlSetCamId);
  }, [
    spotId,
    cameras,
    hasMultipleCams,
    urlSetCamId,
    prevUrlSetCamId,
    primaryCamId,
    device.width,
    hasPremiumCamPermissions,
    multiCamLoaded,
  ]);

  // Handle localStorage updates
  useEffect(() => {
    if (hasMultipleCams && shouldUpdateLocalStorage && multiCamLoaded) {
      // Always save the actual multiCamViewActive state
      // User interactions should be preserved regardless of URL state
      updatePreference(spotId, primaryCamId, multiCamViewActive);
    }
  }, [
    hasMultipleCams,
    multiCamViewActive,
    primaryCamId,
    shouldUpdateLocalStorage,
    spotId,
    multiCamLoaded,
    urlSetCamId,
  ]);

  return {
    multiCamViewActive,
    setMultiCamViewActive,
    primaryCamId,
    setPrimaryCamId,
    multiCamLoaded: { current: multiCamLoaded },
  };
};

export default useCamsPreferenceFromLocalStorage;
